import { Box, Typography } from "@avenue-8/sidekick-ui";
import { PresentationView } from "@cma/presentation-generation-logic";
import styled from "@emotion/styled";
import { Avatar } from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { SingleLogoEditor } from "../SingleLogoEditor";
import { SinglePhotoUploader } from "@cma/shared/components/SinglePhotoUploader";
import { PresentationDisplayMode } from "../../presentation-sections/PresentationSectionProps";

type NavbarProps = {
  $editMode?: boolean;
  $displayMode?: PresentationDisplayMode;
};

const Navbar = styled.div<NavbarProps>`
  position: sticky;
  top: ${(p) => (p.$editMode ? "4rem" : "0")};
  padding: 1rem 0;
  left: 0;
  width: 100%;
  z-index: 100;
  background-color: ${(p) => p.theme.colors.background};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${(p) => p.theme.colors.border2};

  ${(p) =>
    p.$displayMode === "print" &&
    `
    position: static;
    top: auto;
    border-bottom: none;
    padding: 0;
  `}

  ${(p) => p.theme.media.tabletDown} {
    padding: 0.75rem 0;
  }

  @media print {
    padding: 0;
    position: static;
    top: auto;
    border-bottom: none;
  }
`;

const AgentAvatar = styled(Avatar)`
  width: 72px;
  height: 72px;
  font-size: ${(p) => p.theme.text.fontSizes.l};

  ${(p) => p.theme.media.mobileDown} {
    width: 56px;
    height: 56px;
    font-size: ${(p) => p.theme.text.fontSizes.m};
  }
`;

const AgentInfoContainer = styled(Box)`
  display: flex;
  text-align: left;
  align-items: center;
  width: 100%;
  gap: 1rem;

  ${(p) => p.theme.media.mobileDown} {
    gap: 0.75rem;
  }
`;

const AgentInfoContent = styled(Box)`
  display: flex;
  flex-direction: column;
  text-align: left;
  width: 100%;
  p {
    line-height: 1.2;
  }
`;

const AgentName = styled(Typography)`
  font-size: ${(p) => p.theme.text.fontSizes.xl};

  ${(p) => p.theme.media.mobileDown} {
    font-size: ${(p) => p.theme.text.fontSizes.l};
  }
`;

const LogoContent = styled(Box)`
  width: 100%;
  max-width: 220px;
  max-height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: 220px;
    max-height: 72px;
    object-fit: contain;
  }

  ${(p) => p.theme.media.mobileDown} {
    max-width: 128px;
    max-height: 56px;

    img {
      max-width: 128px;
      max-height: 56px;
    }
  }
`;

const LogoPlaceholder = styled.div`
  width: 156px;
  height: 64px;
  background-color: ${(p) => p.theme.colors.background2};
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 0.5rem;
  color: #888;

  ${(p) => p.theme.media.mobileDown} {
    width: 128px;
    height: 56px;
  }
`;

type Props = {
  agent: PresentationView["agent"];
  officeLogo?: string | null;
  editMode?: boolean;
  displayMode?: PresentationDisplayMode;
  onUploadOfficeLogo?: (file: File) => void;
  onDeleteOfficeLogo?: () => void;
  isLoadingOfficeLogo?: boolean;
};

export const PresentationNavbar = (props: Props) => {
  const {
    agent,
    officeLogo,
    editMode,
    displayMode,
    onUploadOfficeLogo,
    onDeleteOfficeLogo,
    isLoadingOfficeLogo,
  } = props;

  if (editMode && onUploadOfficeLogo) {
    return (
      <Navbar $editMode={editMode} $displayMode={displayMode}>
        <AgentInfoContainer>
          <AgentAvatar src={agent.photo ?? ""} alt={agent.name ?? ""} />
          <AgentInfoContent>
            <AgentName mb={0.5}>{agent.name}</AgentName>
            {agent.licenseNumber && (
              <Typography color="text.secondary">
                #{agent.licenseNumber}
              </Typography>
            )}
          </AgentInfoContent>
        </AgentInfoContainer>
        <SinglePhotoUploader
          onFileAccepted={onUploadOfficeLogo}
          onFileRemoved={officeLogo ? onDeleteOfficeLogo : undefined}
          maxSize={5 * 1024 * 1024}
          isLoading={isLoadingOfficeLogo}
        >
          <LogoContent>
            {officeLogo ? (
              <img src={officeLogo} alt="Office Logo" />
            ) : (
              <LogoPlaceholder>
                <PhotoCameraIcon fontSize="small" />
              </LogoPlaceholder>
            )}
          </LogoContent>
        </SinglePhotoUploader>
      </Navbar>
    );
  }

  return (
    <Navbar $editMode={editMode} $displayMode={displayMode}>
      <AgentInfoContainer>
        <AgentAvatar src={agent.photo ?? ""} alt={agent.name ?? ""} />
        <AgentInfoContent>
          <AgentName mb={0.5}>{agent.name}</AgentName>
          {agent.licenseNumber && (
            <Typography color="text.secondary">
              #{agent.licenseNumber}
            </Typography>
          )}
        </AgentInfoContent>
      </AgentInfoContainer>
      <SingleLogoEditor logo={officeLogo} />
    </Navbar>
  );
};
